
function webMessagesApiDebugEnabled() {
  try {
    return (
      new URLSearchParams(window.location.search).get("debugMessages") === "1" ||
      localStorage.getItem("intimsync.messages.debugTools") === "1"
    );
  } catch {
    return false;
  }
}

function debugWebMessagesApiLog(...args: any[]) {
  if (webMessagesApiDebugEnabled()) {
    console.log(...args);
  }
}

import { request } from "../api/http";

export type InboxE2EEMessage = {
  id: string;
  conversationId?: string;
  clientMessageId?: string | null;
  fromUserId?: string | number;
  toUserId?: string | number;
  senderUserId?: string | number;
  recipientUserId?: string | number;
  senderDisplayName?: string;
  senderPublicId?: string;
  cipherText?: string;
  iv?: string;
  createdAt?: string;
  sentAt?: string;
  envelope?: {
    version?: number;
    algorithm?: string;
    ciphertext?: string;
    nonce?: string;
    senderKeyId?: string;
  };
};

export type RecipientEnvelope = {
  recipientUserId: string | number;
  envelope: {
    version: number;
    algorithm: string;
    ciphertext: string;
    nonce: string;
    senderKeyId: string;
  };
};

export async function readReceiptsE2EE(
  conversationId: string,
  messageIds: Array<string | number>,
  options?: { realtime?: boolean; source?: string }
) {
  const ids = messageIds
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0);

  if (!conversationId || !ids.length) return { ok: true };

  const q = new URLSearchParams();
  q.set("conversation_id", String(conversationId));

  if (options?.realtime) {
    q.set("realtime", "1");
    q.set("source", options.source || "sse");
  }

  return request<{ ok: boolean; marked?: number; ignored?: number }>(
    `/messages/read-e2ee?${q.toString()}`,
    {
      method: "POST",
      auth: true,
      body: {
        conversationId,
        conversation_id: conversationId,
        messageIds: ids,
        message_ids: ids,
      },
    }
  );
}

function makeDirectConversationId(payload: {
  toUserId?: string;
  recipientUserId?: string;
  originGroupId?: string | null;
  originActivityId?: string | null;
}) {
  const recipientUserId = String(payload.recipientUserId || payload.toUserId || "");

  if (payload.originActivityId) {
    return `activity:${payload.originActivityId}:dm:${recipientUserId}`;
  }

  if (payload.originGroupId) {
    return `group:${payload.originGroupId}:dm:${recipientUserId}`;
  }

  return `dm:${recipientUserId}`;
}

export async function listMessagesE2EE(
  conversationId: string,
  options?: {
    realtime?: boolean;
    source?: string;
    limit?: number;
    beforeId?: string | number | null;
    before_id?: string | number | null;
  }
) {
  const q = new URLSearchParams();
  q.set("conversation_id", String(conversationId || ""));

  const limit = Number(options?.limit || 100);
  if (Number.isFinite(limit) && limit > 0) {
    q.set("limit", String(Math.min(200, Math.max(1, Math.floor(limit)))));
  }

  const beforeId = options?.beforeId ?? options?.before_id;
  if (beforeId !== null && beforeId !== undefined && String(beforeId).trim()) {
    q.set("before_id", String(beforeId).replace(/^remote:/, "").trim());
  }

  if (options?.realtime) {
    q.set("realtime", "1");
    q.set("source", options.source || "sse");
  }

  const url = `/messages/inbox-e2ee?${q.toString()}`;

  debugWebMessagesApiLog("[Messages web api] listMessagesE2EE", {
    conversationId,
    realtime: !!options?.realtime,
    source: options?.source || "",
    url,
  });

  return request<{ ok?: boolean; items?: InboxE2EEMessage[] }>(
    url,
    { method: "GET", auth: true }
  );
}

export async function sendMessageE2EE(payload: {
  toUserId?: string;
  recipientUserId?: string;
  conversationId?: string;
  contextId?: string | null;
  clientMessageId?: string;
  conversationType?: "direct" | "group" | "activity" | string;
  version?: number;
  algorithm?: string;
  cipherText?: string;
  iv?: string;
  senderKeyId?: string;
  originGroupId?: string | null;
  originActivityId?: string | null;
  recipientEnvelopes?: RecipientEnvelope[];
  mentionedUserIds?: string[];
}) {
  const recipientUserId = String(payload.recipientUserId || payload.toUserId || "");
  const conversationId =
    payload.conversationId ||
    payload.contextId ||
    makeDirectConversationId({
      recipientUserId,
      originGroupId: payload.originGroupId,
      originActivityId: payload.originActivityId,
    });

  const body: any = {
    conversationId,
    contextId: payload.contextId || conversationId,
    clientMessageId: payload.clientMessageId,
    conversationType: payload.conversationType || "direct",
    originGroupId: payload.originGroupId || undefined,
    originActivityId: payload.originActivityId || undefined,
    mentionedUserIds: payload.mentionedUserIds || [],
  };

  if (payload.recipientEnvelopes?.length) {
    body.recipientEnvelopes = payload.recipientEnvelopes;
  } else {
    body.recipientUserId = recipientUserId;
    const explicitEnvelope = (payload as any).envelope;
    body.envelope = explicitEnvelope || {
      version: Number((payload as any).version || 1),
      algorithm: (payload as any).algorithm || "DEV-PLAINTEXT-BASE64",
      ciphertext: payload.cipherText || (payload as any).ciphertext || "",
      nonce: payload.iv || (payload as any).nonce || "",
      senderKeyId: (payload as any).senderKeyId || "web-dev-plain",
    };
  }

  if (payload.mentionedUserIds?.length) {
    body.mentionedUserIds =
      payload.mentionedUserIds;
  }

  return request<InboxE2EEMessage>(`/messages/send-e2ee`, {
    method: "POST",
    auth: true,
    body,
  });
}

export async function listMessageDeleteEvents(conversationId: string) {
  if (!conversationId) return [];

  const q = new URLSearchParams();
  q.set("conversationId", conversationId);

  const res = await request<{ ok?: boolean; items?: any[] }>(
    `/messages/delete-events?${q.toString()}`,
    { method: "GET", auth: true }
  );

  return Array.isArray(res?.items) ? res.items : [];
}

export async function deleteMessageItemWeb(input: {
  conversationId: string;
  itemKind: "text" | "media";
  itemId: string;
  scope: "me" | "everyone";
}) {
  return request<any>("/messages/delete-item", {
    method: "POST",
    auth: true,
    body: input,
  });
}

export async function listMediaManifestsWeb(conversationId: string) {
  if (!conversationId) return [];

  const q = new URLSearchParams();
  q.set("conversationId", conversationId);

  const res = await request<{ ok?: boolean; items?: any[] }>(
    `/messages/media-e2ee?${q.toString()}`,
    { method: "GET", auth: true }
  );

  return Array.isArray(res?.items) ? res.items : [];
}

export async function sendTypingHintWeb(input: {
  conversationId: string;
  typing?: boolean;
}) {
  return request<any>("/messages/typing", {
    method: "POST",
    auth: true,
    body: {
      conversationId: input.conversationId,
      typing: input.typing !== false,
    },
  });
}

export async function deliveredReceiptsE2EE(
  conversationId: string,
  identifiers: {
    messageIds?: Array<string | number>;
    clientMessageIds?: string[];
  },
  options?: {
    realtime?: boolean;
    source?: string;
  },
) {
  const token =
    window.localStorage.getItem("intimsync.access_token") ||
    window.localStorage.getItem("access_token") ||
    window.localStorage.getItem("jwt") ||
    window.localStorage.getItem("admin_jwt") ||
    "";

  const query = new URLSearchParams({
    conversation_id: String(conversationId),
  });

  if (options?.realtime) {
    query.set("realtime", "1");
  }

  if (options?.source) {
    query.set("source", String(options.source));
  }

  const response = await fetch(
    `/api/v1/messages/delivered-e2ee?${query.toString()}`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(token
          ? {
              Authorization: token.startsWith("Bearer ")
                ? token
                : `Bearer ${token}`,
            }
          : {}),
      },
      body: JSON.stringify({
        conversationId,
        messageIds: identifiers.messageIds || [],
        clientMessageIds: identifiers.clientMessageIds || [],
      }),
    },
  );

  const payload = await response
    .json()
    .catch(() => null);

  if (!response.ok) {
    const error: any = new Error(
      payload?.error ||
      payload?.message ||
      `delivered_receipt_http_${response.status}`,
    );

    error.status = response.status;
    error.data = payload;

    throw error;
  }

  return payload;
}
